import React, { useState } from 'react';
import SubmitButton from '@/ctComponents/submitButton';
import containerStyles from '@/screens/Autobook/containerList.module.css';
import { AutobookRuleStepWarehouseCustomer } from './AutobookRuleStepWarehouseCustomer';
import { AutobookRuleStepLiveUnload } from './AutobookRuleStepLiveUnload';
import { AutobookRuleStepDropPick } from './AutobookRuleStepDropPick';

export const AUTOBOOK_RULE_WIZARD_STEPS = [
  { key: 'warehouse_customer', label: 'Selecting Warehouse & Costumer' },
  { key: 'live_unload', label: 'Live Unload' },
  { key: 'drop_pick', label: 'Drop & Pick' },
];

/**
 * @param {object} p
 * @param {'add'|'edit'} [p.mode]
 * @param {() => void} p.onSubmit
 * @param {boolean} [p.submitLoading]
 * @param {boolean} [p.submitDisabled]
 * @param {(stepIndex: number) => boolean} [p.getStepCanProceed]
 */
export function useAutobookRuleWizard({
  mode = 'add',
  onSubmit,
  submitLoading = false,
  submitDisabled = false,
  getStepCanProceed = () => true,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const primaryLabel = mode === 'add' ? 'Create' : 'Save';
  const primaryDisabled =
    submitDisabled || submitLoading || !getStepCanProceed(activeStep);

  const handlePrimaryClick = () => {
    if (!primaryDisabled && onSubmit) onSubmit();
  };

  return {
    activeStep,
    setActiveStep,
    primaryLabel,
    primaryDisabled,
    handlePrimaryClick,
    submitLoading,
  };
}

export const AutobookRuleFormWizardPanel = ({
  activeStep,
  setActiveStep,
  submitLoading,
  deliveryTypeOption,
  setDeliveryTypeOption,
  maximumDaysAfterWindow,
  setMaximumDaysAfterWindow,
  deliveryTypeOptions,
  draftCustomers,
  draftWarehouses,
  draftWarehouseGroups,
  selectedWarehouseCatalogIds,
  setSelectedWarehouseCatalogIds,
  selectedGroupCatalogIds,
  setSelectedGroupCatalogIds,
  draftRuleUuid,
  liveUnloadTime,
  setLiveUnloadTime,
  liveUnloadDriveToPortMin,
  setLiveUnloadDriveToPortMin,
  liveUnloadAutobookBackwardWindowHour,
  setLiveUnloadAutobookBackwardWindowHour,
  liveUnloadAutobookForwardWindowHour,
  setLiveUnloadAutobookForwardWindowHour,
  rulePreferences,
  onRulePreferencesChange,
  swRulePreferences,
  onSwRulePreferencesChange,
  preferencesSyncKey,
  swUseOldestEmptyFirst,
  setSwUseOldestEmptyFirst,
  swSameGroup,
  setSwSameGroup,
  swSameCity,
  setSwSameCity,
  swMaxDistance,
  setSwMaxDistance,
  swMinExcessThreshold,
  setSwMinExcessThreshold,
  swBeforeTargerReturn,
  setSwBeforeTargerReturn,
  swAfterTargerReturn,
  setSwAfterTargerReturn,
  selectedSwSharingGroupCatalogIds,
  setSelectedSwSharingGroupCatalogIds,
  scopeSelectionLocked = false,
}) => (
  <div className="box-border w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-solid border-[#e8e8e8] bg-[#F9FAFB] p-4">
    <div className="flex w-full min-w-0 max-w-full flex-col gap-4">
      <div
        className="grid w-full min-w-0 max-w-full gap-2"
        style={{
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        }}>
        {AUTOBOOK_RULE_WIZARD_STEPS.map((step, index) => (
          <button
            type="button"
            key={step.key}
            className={`${containerStyles.tabInItem} w-full min-w-0 ${
              index === activeStep ? containerStyles.selected : ''
            }`}
            onClick={() => !submitLoading && setActiveStep(index)}>
            <div className={containerStyles.tabContent}>
              <span className="min-w-0 text-center leading-snug">
                {step.label}
              </span>
            </div>
          </button>
        ))}
      </div>

      <div className="w-full min-w-0 max-w-full">
        {activeStep === 0 && (
          <AutobookRuleStepWarehouseCustomer
            deliveryTypeOption={deliveryTypeOption}
            setDeliveryTypeOption={setDeliveryTypeOption}
            maximumDaysAfterWindow={maximumDaysAfterWindow}
            setMaximumDaysAfterWindow={setMaximumDaysAfterWindow}
            deliveryTypeOptions={deliveryTypeOptions}
            catalogCustomers={draftCustomers}
            catalogWarehouses={draftWarehouses}
            catalogWarehouseGroups={draftWarehouseGroups}
            selectedWarehouseCatalogIds={selectedWarehouseCatalogIds}
            setSelectedWarehouseCatalogIds={setSelectedWarehouseCatalogIds}
            selectedGroupCatalogIds={selectedGroupCatalogIds}
            setSelectedGroupCatalogIds={setSelectedGroupCatalogIds}
            draftRuleUuid={draftRuleUuid}
            scopeSelectionLocked={scopeSelectionLocked}
          />
        )}
        {activeStep === 1 && (
          <AutobookRuleStepLiveUnload
            unloadTime={liveUnloadTime}
            setUnloadTime={setLiveUnloadTime}
            driveToPortMin={liveUnloadDriveToPortMin}
            setDriveToPortMin={setLiveUnloadDriveToPortMin}
            autobookBackwardWindowHour={liveUnloadAutobookBackwardWindowHour}
            setAutobookBackwardWindowHour={
              setLiveUnloadAutobookBackwardWindowHour
            }
            autobookForwardWindowHour={liveUnloadAutobookForwardWindowHour}
            setAutobookForwardWindowHour={
              setLiveUnloadAutobookForwardWindowHour
            }
            rulePreferences={rulePreferences}
            onRulePreferencesChange={onRulePreferencesChange}
            preferencesSyncKey={preferencesSyncKey}
          />
        )}
        {activeStep === 2 && (
          <AutobookRuleStepDropPick
            swUseOldestEmptyFirst={swUseOldestEmptyFirst}
            setSwUseOldestEmptyFirst={setSwUseOldestEmptyFirst}
            swSameWarehouseReuse={swSameGroup}
            setSwSameWarehouseReuse={setSwSameGroup}
            swSameCityReuse={swSameCity}
            setSwSameCityReuse={setSwSameCity}
            swMaxDistance={swMaxDistance}
            setSwMaxDistance={setSwMaxDistance}
            swMinExcessThreshold={swMinExcessThreshold}
            setSwMinExcessThreshold={setSwMinExcessThreshold}
            swBeforeTargerReturn={swBeforeTargerReturn}
            setSwBeforeTargerReturn={setSwBeforeTargerReturn}
            swAfterTargerReturn={swAfterTargerReturn}
            setSwAfterTargerReturn={setSwAfterTargerReturn}
            catalogWarehouseGroups={draftWarehouseGroups}
            selectedSwSharingGroupCatalogIds={selectedSwSharingGroupCatalogIds}
            setSelectedSwSharingGroupCatalogIds={
              setSelectedSwSharingGroupCatalogIds
            }
            catalogCustomers={draftCustomers}
            catalogWarehouses={draftWarehouses}
            draftRuleUuid={draftRuleUuid}
            swRulePreferences={swRulePreferences}
            onSwRulePreferencesChange={onSwRulePreferencesChange}
            preferencesSyncKey={preferencesSyncKey}
          />
        )}
      </div>
    </div>
  </div>
);

export const AutobookRuleFormWizardToolbar = ({
  submitLoading,
  primaryLabel,
  primaryDisabled,
  handlePrimaryClick,
}) => (
  <div
    className="box-border flex w-full min-w-0 max-w-full flex-row items-center justify-end"
    style={{ marginTop: 0 }}>
    <SubmitButton
      text={primaryLabel}
      onClick={handlePrimaryClick}
      disable={primaryDisabled}
      loading={submitLoading}
    />
  </div>
);
